import moment from 'moment';
import momentTz from "moment-timezone";

// Compare two arrays to see if they match
export const arrayEqualsCheck = (a, b) => {
	// If they point to the same instance of the array
	if (a === b)
		return true;

	// If they point to the same instance of date
	if (a instanceof Date && b instanceof Date)
		return a.getTime() === b.getTime();

	// If both of them are not null and their type is not an object
	if (!a || !b || (typeof a !== 'object' && typeof b !== 'object'))
		return a === b;

	// This means the elements are objects
	// If they are not the same type of objects
	if (a.prototype !== b.prototype)
		return false;

	// Check if both of the objects have the same number of keys
	const keys = Object.keys(a);
	if (keys.length !== Object.keys(b).length)
		return false;

	// Check recursively for every key in both
	return keys.every(k => equalsCheck(a[k], b[k]));
};

// Check if valid email
export const validateEmail = (email) => {
	return String(email)
		.toLowerCase()
		.match(
			/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
		);
};

// sleep
export function sleep(ms) {
	return new Promise(resolve => setTimeout(resolve, ms));
};

// Remove empty values from object
export function removeEmptyFromObj(obj) {
	for (var propName in obj) {
		if (obj[propName] === null || obj[propName] === undefined || obj[propName] === '') {
			delete obj[propName];
		}
	}
	return obj
};

// Get age from birthdate
export const getAgeFromBirthdate = (dateString) => {
	if (!dateString) {
		return 0;
	}
	var today = new Date();
	var birthDate = new Date(dateString);
	var age = today.getFullYear() - birthDate.getFullYear();
	var m = today.getMonth() - birthDate.getMonth();
	if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
		age--;
	}

	if (isNaN(age) || age < 0) {
		age = 0;
	}
	return age;
};

export const sortByCreatedAt = (a, b) => {
	if (a.createdAt === b.createdAt) return 0;
	if (a.createdAt > b.createdAt) return -1;
	return 1;
};

export const renameKeys = (keysMap, obj) => {
	return Object.keys(obj).reduce((acc, key) => ({
		...acc,
		...{
			[keysMap[key] || key]: obj[key]
		}
	}), {});
};

/**
 * diffObjects
 * 
 * Find the differences between two objects and push to a new object
 * (c) 2019 Chris Ferdinandi & Jascha Brinkmann, MIT License, https://gomakethings.com & https://twitter.com/jaschaio
 * @param  {Object} obj1 The original object
 * @param  {Object} obj2 The object to compare against it
 * @return {Object}      An object of differences between the two
 */
export const diffObjects = (obj1, obj2) => {
	// Make sure an object to compare is provided
	if (!obj2 || Object.prototype.toString.call(obj2) !== '[object Object]') {
		return obj1;
	}

	let diffs = {};
	let key;

	/**
	 * Check if two arrays are equal
	 * @param  {Array}   arr1 The first array
	 * @param  {Array}   arr2 The second array
	 * @return {Boolean}      If true, both arrays are equal
	 */
	const arraysMatch = (arr1, arr2) => {
		// Check if the arrays are the same length
		if (arr1.length !== arr2.length) return false;

		// Check if all items exist and are in the same order
		for (var i = 0; i < arr1.length; i++) {
			if (arr1[i] !== arr2[i]) return false;
		}

		// Otherwise, return true
		return true;
	};

	/**
	 * Compare two items and push non-matches to object
	 * @param  {object}      item1 The first item
	 * @param  {object}      item2 The second item
	 * @param  {String} key   The key in our object
	 */
	const compare = (item1, item2, key) => {
		// Get the object type
		let type1 = Object.prototype.toString.call(item1);
		let type2 = Object.prototype.toString.call(item2);

		// If type2 is undefined it has been removed
		if (type2 === '[object Undefined]') {
			diffs[key] = null;
			return;
		}

		// If items are different types
		if (type1 !== type2) {
			diffs[key] = item2;
			return;
		}

		// If an object, compare recursively
		if (type1 === '[object Object]') {
			let objDiff = diffObjects(item1, item2);
			if (Object.keys(objDiff).length > 0) {
				diffs[key] = objDiff;
			}
			return;
		}

		// If an array, compare
		if (type1 === '[object Array]') {
			if (!arraysMatch(item1, item2)) {
				diffs[key] = item2;
			}
			return;
		}

		// Else if it's a function, convert to a string and compare
		// Otherwise, just compare
		if (type1 === '[object Function]') {
			if (item1.toString() !== item2.toString()) {
				diffs[key] = item2;
			}
		} else {
			if (item1 !== item2) {
				diffs[key] = item2;
			}
		}
	};

	// Compare our objects
	// Loop through the first object
	for (key in obj1) {
		compare(obj1[key], obj2[key], key);
	}

	// Loop through the second object and find missing items
	for (key in obj2) {
		if (!obj1[key] && obj1[key] !== obj2[key]) {
			diffs[key] = obj2[key];
		}
	}

	// Return the object of differences
	return diffs;
};

export const formatDate = (timestamp = Date.now(), formatString = "MMMM D, YYYY hh:mm:ss A", tZone = Intl.DateTimeFormat().resolvedOptions().timeZone) => {
	if (!timestamp) return timestamp; // Invalid timestamp
	if (tZone === null) {
		return momentTz(timestamp).format(formatString);
	}
	return momentTz(timestamp).tz(tZone).format(formatString);
}

export const formatPhone = (phoneNumberString) => {
	const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
	const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
	if (match) {
		const intlCode = (match[1] ? '+1 ' : '');
		return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
	}
	return phoneNumberString;
}

export const isValidPhone = (phoneNumberString) => {
	const phoneCheckRegex = /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/g;
	return phoneNumberString.match(phoneCheckRegex);
}

export const formatSSN = (ssn) => {
	if (!ssn) {
		return '';
	}
	let val = ssn.replace(/\D/g, '');
	if (val.length != 9) {
		return ssn;
	}
	val = val.replace(/^(\d{3})/, '$1-');
	val = val.replace(/-(\d{2})/, '-$1-');
	val = val.replace(/(\d)-(\d{4}).*/, '$1-$2');
	return val;
}

export const isValidSSN = (ssn = '') => {
	const ssnCheckRegex = /^(?!(000|666|9))\d{3}-(?!00)\d{2}-(?!0000)\d{4}$/g;
	return ssn.match(ssnCheckRegex)
}

// Use on GET requests to add query params to the URL
export const getParams = (params) => (params != null && typeof params === 'object') ? "?" + Object.entries(params).map(([key, value]) => `${key}=${value.toString().trim().replace(/ /g, '%20')}`).join("&") : "";
